export const normalizeRole = (role) => String(role || '').trim().toLowerCase();

/**
 * Checks whether an employee's role string contains a target position.
 * Handles comma, slash, and semicolon separated multi-roles
 * (e.g. "Floor Supervisor,Dealer", "Hosts,Waitress", "Waitress,Bartender", "Dealer,Waitress").
 * Also handles singular/plural variants and aliases (e.g. "waitress"/"server", "host"/"hosts").
 */
export const employeeMatchesPosition = (employeeRole, targetPosition) => {
  if (!employeeRole || !targetPosition) return false;
  const roles = String(employeeRole)
    .split(/[,/;\\]+/)
    .map(normalizeRole)
    .filter(Boolean);
  const target = normalizeRole(targetPosition);

  return roles.some((role) => {
    if (role === target) return true;

    // Dealer
    const isDealerTarget = target === 'dealer' || target === 'dealers';
    const isDealerRole = role === 'dealer' || role === 'dealers';
    if (isDealerTarget && isDealerRole) return true;

    // Waitress / Server
    const isWaitressTarget =
      target === 'waitress' ||
      target === 'waitresses' ||
      target === 'server' ||
      target === 'servers' ||
      target === 'waiter' ||
      target === 'waiters' ||
      target === 'server/waitress' ||
      target === 'server / waitress' ||
      target === 'waitress/server' ||
      target === 'waitress / server';
    const isWaitressRole =
      role === 'waitress' ||
      role === 'waitresses' ||
      role === 'server' ||
      role === 'servers' ||
      role === 'waiter' ||
      role === 'waiters' ||
      role === 'server/waitress' ||
      role === 'server / waitress' ||
      role === 'waitress/server' ||
      role === 'waitress / server';
    if (isWaitressTarget && isWaitressRole) return true;

    // Floor Supervisor
    const isFloorTarget =
      target === 'floor supervisor' ||
      target === 'floorsupervisor' ||
      target === 'floor-supervisor' ||
      target === 'supervisor' ||
      target === 'floor';
    const isFloorRole =
      role === 'floor supervisor' ||
      role === 'floorsupervisor' ||
      role === 'floor-supervisor' ||
      role === 'supervisor' ||
      role === 'floor';
    if (isFloorTarget && isFloorRole) return true;

    // Bartender
    const isBartenderTarget = target === 'bartender' || target === 'bartenders' || target === 'bar';
    const isBartenderRole = role === 'bartender' || role === 'bartenders' || role === 'bar';
    if (isBartenderTarget && isBartenderRole) return true;

    // Host
    const isHostTarget = target === 'host' || target === 'hosts' || target === 'hostess';
    const isHostRole = role === 'host' || role === 'hosts' || role === 'hostess';
    if (isHostTarget && isHostRole) return true;

    // Cashier
    const isCashierTarget = target === 'cashier' || target === 'cashiers';
    const isCashierRole = role === 'cashier' || role === 'cashiers';
    if (isCashierTarget && isCashierRole) return true;

    // Janitor / Custodian
    const isJanitorTarget =
      target === 'janitor' ||
      target === 'janitors' ||
      target === 'custodian' ||
      target === 'custodians' ||
      target === 'custodian/janitor' ||
      target === 'custodian / janitor' ||
      target === 'janitor/custodian' ||
      target === 'janitor / custodian' ||
      target === 'cleaner';
    const isJanitorRole =
      role === 'janitor' ||
      role === 'janitors' ||
      role === 'custodian' ||
      role === 'custodians' ||
      role === 'custodian/janitor' ||
      role === 'custodian / janitor' ||
      role === 'janitor/custodian' ||
      role === 'janitor / custodian' ||
      role === 'cleaner';
    if (isJanitorTarget && isJanitorRole) return true;

    // Cook
    const isCookTarget = target === 'cook' || target === 'cooks' || target === 'chef' || target === 'kitchen';
    const isCookRole = role === 'cook' || role === 'cooks' || role === 'chef' || role === 'kitchen';
    if (isCookTarget && isCookRole) return true;

    // Busser
    const isBusserTarget = target === 'busser' || target === 'bussers';
    const isBusserRole = role === 'busser' || role === 'bussers';
    if (isBusserTarget && isBusserRole) return true;

    // Other / Dynamic positions (matches any non-core role)
    if (target === 'other' || target === 'others') {
      const coreRoles = [
        'dealer', 'dealers',
        'waitress', 'waitresses', 'server', 'servers', 'waiter', 'waiters', 'server/waitress', 'server / waitress',
        'floor supervisor', 'floorsupervisor', 'floor-supervisor', 'supervisor', 'floor',
        'bartender', 'bartenders', 'bar',
        'host', 'hosts', 'hostess',
        'cashier', 'cashiers',
        'janitor', 'janitors', 'custodian', 'custodians', 'custodian/janitor', 'custodian / janitor',
        'cook', 'cooks', 'chef', 'kitchen'
      ];
      if (!coreRoles.includes(role)) return true;
    }

    return false;
  });
};

/**
 * Filters a list of employees to only those matching target position.
 * Strictly returns only matching employees. Multi-role employees (e.g. "Dealer,Waitress")
 * will show in both matching modules, but never in unrelated modules.
 */
export const filterEmployeesByPosition = (employees = [], targetPosition) => {
  if (!Array.isArray(employees)) return [];
  return employees.filter((emp) => {
    if (!emp) return false;
    const role = typeof emp === 'object' ? (emp?.role || emp?.emprole || emp?.position || '') : '';
    return employeeMatchesPosition(role, targetPosition);
  });
};

/**
 * Determines whether a given tip module position supports Owe To Toast.
 * Both Waitress / Server and Bartender modules support Owe To Toast.
 */
export const positionSupportsOweToToast = (position = "") => {
  const p = String(position || "").trim().toLowerCase();
  return (
    p === "bartender" ||
    p === "bartenders" ||
    p === "waitress" ||
    p === "waitresses" ||
    p === "server" ||
    p === "servers" ||
    p === "server/waitress" ||
    p === "waitress/server" ||
    p.includes("bartender") ||
    p.includes("waitress") ||
    p.includes("server")
  );
};

